/**
 * Campaign attribution + property sanitization.
 *
 * Split out of `src/lib/marketing/analytics.ts` so that the analytics core
 * (`src/lib/analytics.ts`) can register attribution as PostHog super
 * properties without importing the marketing funnel module (which imports the
 * core in turn). This module has no analytics dependencies of its own.
 *
 * Two attribution scopes are kept:
 *  - **current session** (`sessionStorage`) — the touch that started this
 *    browsing session. Refreshed only when a *new* campaign-tagged entry
 *    happens in the same session; ordinary internal navigation never
 *    overwrites it.
 *  - **durable first touch** (`localStorage`) — the very first campaign the
 *    browser ever arrived from. Written once and never overwritten, so
 *    conversions that happen days later stay attributable.
 *
 * Only the UTM allowlist, the referring host and its path are ever stored.
 * Full query strings, emails, and free text never leave the browser.
 */

export type MarketingProps = Record<string, string | number | boolean | null | undefined>;

/** sessionStorage key for the current-session touch. */
export const ATTRIBUTION_STORAGE_KEY = "ka_marketing_attribution_v1";
/** localStorage key for the durable first touch. */
export const FIRST_TOUCH_STORAGE_KEY = "ka_marketing_first_touch_v1";

/** UTM params we are willing to record. Everything else in the query is dropped. */
export const ALLOWED_UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/**
 * Property keys that may never be sent, regardless of caller. Matching is
 * substring-based and case-insensitive so `customer_email` or `bodyText` are
 * caught as well.
 */
const FORBIDDEN_KEY_FRAGMENTS = [
  "email",
  "name",
  "message",
  "body",
  "text",
  "comment",
  "phone",
  "address",
  "query",
  "search",
  "token",
  "secret",
  "password",
  "auth",
  "session",
  "user_id",
  "userid",
  "ip",
];

const SAFE_KEY_ALLOWLIST = new Set([
  "platform",
  "surface",
  "placement",
  "slug",
  "track_slug",
  "release_state",
  "status",
  "type",
  "source",
  "destination",
  "action",
  "path",
  "campaign_slug",
  "duplicate",
]);

const MAX_STRING_LENGTH = 96;
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer_host?: string;
  referrer_path?: string;
  landing_path?: string;
}

/**
 * Normalizes a pathname for grouping: strips origin, query and hash, lowercases,
 * and removes a trailing slash (except for the root path).
 */
export function normalizePath(input: string | null | undefined): string {
  if (!input) return "/";
  let path = input;
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return "/";
    }
  }
  path = path.split("?")[0].split("#")[0].toLowerCase();
  if (!path.startsWith("/")) path = `/${path}`;
  if (path.length > 1) path = path.replace(/\/+$/, "");
  return path || "/";
}

function safeValue(value: unknown): string | number | boolean | null | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "boolean" || typeof value === "number") {
    return Number.isFinite(value as number) || typeof value === "boolean"
      ? (value as number | boolean)
      : undefined;
  }
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  // Query strings are dropped entirely before any further inspection.
  const withoutQuery = trimmed.split("?")[0].trim();
  if (!withoutQuery) return undefined;
  if (EMAIL_RE.test(withoutQuery)) return undefined;
  return withoutQuery.slice(0, MAX_STRING_LENGTH);
}

/**
 * Drops PII-shaped keys and unsafe values. Keys are kept when they are on the
 * explicit allowlist or when they contain no forbidden fragment.
 */
export function sanitizeProps(props?: MarketingProps): MarketingProps {
  const out: MarketingProps = {};
  if (!props) return out;
  for (const [key, raw] of Object.entries(props)) {
    const lower = key.toLowerCase();
    if (!SAFE_KEY_ALLOWLIST.has(lower) && FORBIDDEN_KEY_FRAGMENTS.some((f) => lower.includes(f))) continue;
    const value = safeValue(raw);
    if (value === undefined) continue;
    out[key] = value;
  }
  return out;
}

function readJson(storage: Storage | undefined, key: string): Attribution | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Attribution;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeJson(storage: Storage | undefined, key: string, value: Attribution): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — attribution simply is not persisted */
  }
}

function readStoredAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  return readJson(window.sessionStorage, ATTRIBUTION_STORAGE_KEY);
}

/** True when the attribution carries at least one campaign parameter. */
export function hasCampaign(attribution: Attribution): boolean {
  return ALLOWED_UTM_PARAMS.some((p) => Boolean(attribution[p]));
}

/**
 * Derives safe attribution from a URL + referrer. Only the allowlisted UTM
 * params, the referring host and its normalized path are kept — never the
 * full query string.
 */
export function deriveAttribution(url: string, referrer: string, currentHost?: string): Attribution {
  const attribution: Attribution = {};
  let parsed: URL | null = null;
  try {
    parsed = new URL(url);
  } catch {
    parsed = null;
  }
  if (parsed) {
    attribution.landing_path = normalizePath(parsed.pathname);
    for (const param of ALLOWED_UTM_PARAMS) {
      const value = safeValue(parsed.searchParams.get(param));
      if (typeof value === "string") attribution[param] = value;
    }
  }
  if (referrer) {
    try {
      const ref = new URL(referrer);
      const host = ref.hostname.toLowerCase();
      if (!currentHost || host !== currentHost.toLowerCase()) {
        attribution.referrer_host = host;
        attribution.referrer_path = normalizePath(ref.pathname);
      }
    } catch {
      /* ignore unparsable referrers */
    }
  }
  return attribution;
}

/**
 * Captures attribution for the current browsing session and, the first time
 * ever, the durable first touch.
 *
 * - Nothing stored yet → store the current derivation in both scopes.
 * - Already stored, and this navigation carries NO campaign params → keep the
 *   existing session attribution untouched.
 * - Already stored, and this navigation DOES carry campaign params → refresh
 *   the session attribution (a genuinely new campaign click), while the
 *   durable first touch stays as it was.
 */
export function ensureAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  const fresh = deriveAttribution(window.location.href, document.referrer || "", window.location.hostname);
  const existing = readStoredAttribution();

  let session: Attribution;
  if (!existing) {
    session = fresh;
    writeJson(window.sessionStorage, ATTRIBUTION_STORAGE_KEY, session);
  } else if (hasCampaign(fresh)) {
    session = fresh;
    writeJson(window.sessionStorage, ATTRIBUTION_STORAGE_KEY, session);
  } else {
    session = existing;
  }

  if (!readJson(window.localStorage, FIRST_TOUCH_STORAGE_KEY)) {
    writeJson(window.localStorage, FIRST_TOUCH_STORAGE_KEY, session);
  }

  return session;
}

/** Current session attribution (empty object when nothing was captured). */
export function getAttribution(): Attribution {
  return readStoredAttribution() ?? {};
}

/** Durable first-touch attribution (empty object when nothing was captured). */
export function getFirstTouchAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  return readJson(window.localStorage, FIRST_TOUCH_STORAGE_KEY) ?? {};
}

/**
 * Attribution rendered as PostHog super properties: current-session values
 * under their plain names, durable first-touch values prefixed `first_touch_`.
 * Sanitized, so nothing PII-shaped can slip in.
 */
export function attributionSuperProps(): MarketingProps {
  if (typeof window === "undefined") return {};
  const session = ensureAttribution();
  const first = getFirstTouchAttribution();
  const props: MarketingProps = { ...sanitizeProps(session as MarketingProps) };
  for (const [key, value] of Object.entries(sanitizeProps(first as MarketingProps))) {
    props[`first_touch_${key}`] = value;
  }
  return props;
}
